import { useEffect, useState } from "react";
import { Label, Menu, Footer, GateVignette, CookieBanner } from "../components";
import { Language } from "../constants";
import { useA11y, useStrings } from "../i18n";

interface LandingProps {
  lang: Language;
  setLang: (lang: Language) => void;
}

type PermissionState = "idle" | "requesting" | "redirecting" | "denied";

function isWithinOpenHours(): boolean {
  const hour = new Date().getHours();
  return true;
  return hour >= 7 && hour < 19;
}

export function Landing({ lang, setLang }: LandingProps) {
  const Strings = useStrings(lang);
  const A11y = useA11y(lang);

  useEffect(() => {
    document.title = Strings.pageTitle;
  }, [Strings]);
  const [permissionState, setPermissionState] =
    useState<PermissionState>("idle");
  const isOpen = isWithinOpenHours();

  const statusMessage =
    permissionState === "requesting"  ? Strings.requestingAccess :
    permissionState === "redirecting" ? Strings.redirectingToExperience :
    permissionState === "denied"      ? `${Strings.cameraDenied}. ${Strings.cameraDeniedDesc}` :
    "";

  const requestPermissions = async () => {
    setPermissionState("requesting");
    try {
      // Request device motion permission first (required for AR on iOS 13+)
      // Must be called directly from user gesture context
      if (
        typeof DeviceMotionEvent !== "undefined" &&
        typeof (DeviceMotionEvent as any).requestPermission === "function"
      ) {
        const motionResult = await (
          DeviceMotionEvent as any
        ).requestPermission();
        if (motionResult === "denied") {
          throw new Error("Motion permission denied");
        }
      }

      // Request device orientation permission
      if (
        typeof DeviceOrientationEvent !== "undefined" &&
        typeof (DeviceOrientationEvent as any).requestPermission === "function"
      ) {
        const orientationResult = await (
          DeviceOrientationEvent as any
        ).requestPermission();
        if (orientationResult === "denied") {
          throw new Error("Orientation permission denied");
        }
      }

      // Request camera permission last
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      stream.getTracks().forEach((track) => track.stop());

      // Redirect after permissions granted
      setPermissionState("redirecting");
      localStorage.setItem("viewer-lang", lang);
      window.location.href = "/ar/viewer.html" + window.location.search;
    } catch {
      setPermissionState("denied");
    }
  };

  return (
    <div className="min-h-dvh flex flex-col bg-stone-50">
      {/* Title */}
      <header className="px-6 pt-2 pb-4">
        <div className="flex items-center justify-between pb-3 mb-4 border-b-[0.5px] border-stone-900">
          <Label className="text-terra-700">{Strings.fieldGuide}</Label>
          <Menu lang={lang} setLang={setLang} />
        </div>
        <h1 className="font-display font-medium text-[40px] leading-[0.96] tracking-tight">
          {Strings.title}{" "}
          <em className="text-terra-500 font-normal">
            {Strings.titleHighlight}
          </em>
        </h1>
        <p className="mt-5 text-[14px] leading-relaxed text-stone-700">
          {Strings.landingDescription}
        </p>
      </header>

      <main className="flex-1 flex flex-col">
        <div className="flex-1" />

      {/* Gate Image */}
      <div className="px-6 flex justify-center">
        <GateVignette alt={A11y.gateImageAlt} />
      </div>

      {/* Permissions */}
      <div className="px-6 pt-5 pb-2">
        <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>

        {/* After hours - AR disabled */}
        {!isOpen && (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-stone-200 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-stone-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"
                />
              </svg>
            </div>
            <h2 className="font-display font-medium text-lg mb-2">
              {Strings.afterHours}
            </h2>
            <p className="font-serif italic text-[14px] text-stone-600 mb-4">
              {Strings.afterHoursDesc}
            </p>
            <button
              disabled
              className="w-full h-[52px] px-[22px] bg-stone-200 text-stone-400 border border-stone-200 font-serif text-base font-medium flex items-center justify-between cursor-not-allowed"
            >
              <span>{Strings.enableCamera}</span>
              <span className="font-mono text-sm" aria-hidden="true">→</span>
            </button>
          </div>
        )}

        {isOpen && permissionState === "idle" && (
          <>
            <h2 className="font-display font-medium text-lg mb-3">
              {Strings.beforeYouBegin}
            </h2>
            <ul className="font-serif text-[14px] text-stone-700 mb-5 space-y-2">
              <li className="flex gap-2">
                <span className="text-terra-700" aria-hidden="true">•</span>
                <span>{Strings.tipVolume}</span>
              </li>
              <li className="flex gap-2">
                <span className="text-terra-700" aria-hidden="true">•</span>
                <span>{Strings.tipCamera}</span>
              </li>
            </ul>
            <button
              onClick={requestPermissions}
              className="w-full h-[52px] px-[22px] bg-terra-500 text-white border border-terra-500 font-serif text-base font-medium flex items-center justify-between cursor-pointer hover:-translate-y-px transition-transform"
            >
              <span>{Strings.enableCamera}</span>
              <span className="font-mono text-sm" aria-hidden="true">→</span>
            </button>
          </>
        )}

        {isOpen && (permissionState === "requesting" || permissionState === "redirecting") && (
          <div className="flex items-center justify-center gap-3 py-4">
            <div
              className="w-5 h-5 border-2 border-terra-500 border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
            <span className="text-stone-700">
              {permissionState === "requesting" ? Strings.requestingAccess : Strings.redirectingToExperience}
            </span>
          </div>
        )}

        {isOpen && permissionState === "denied" && (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-terra-700/10 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-terra-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="font-display font-medium text-lg mb-2">
              {Strings.cameraDenied}
            </h2>
            <p className="font-serif italic text-[14px] text-stone-700 mb-4">
              {Strings.cameraDeniedDesc}
            </p>
            <button
              onClick={requestPermissions}
              className="w-full h-[52px] px-[22px] bg-transparent text-stone-900 border border-stone-900 font-serif text-base font-medium flex items-center justify-between cursor-pointer hover:-translate-y-px transition-transform"
            >
              <span>{Strings.tryAgain}</span>
              <span className="font-mono text-sm" aria-hidden="true">→</span>
            </button>
          </div>
        )}

      </div>
      </main>

      <Footer lang={lang} />
      <CookieBanner lang={lang} />
    </div>
  );
}
